import { useContext } from 'react';
import { getPatientQueryOptions } from '@/api/queries/patients';
import { BackButton } from '@/components/ui/backButton';
import { Overview } from '@/components/patientDetails/overview/overview';
import { Tabs } from '@/components/ui/tabs';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { isFullPatient, isNotNull } from '@/lib/utils';
import { Assessments } from '@/components/patientDetails/assessments/assessments';
import { TFunction } from 'i18next';
import { ErrorComponent } from '@/components/generalError';
import { EligibilityChecklistTab } from '@/components/patientDetails/eligibilityChecklist/eligibilityChecklistTab';
import { PatientDetailsProvider } from '@/context/patient/patientDetailsContext';
import { usePermission } from '@/hooks/usePermission';
import { Subject } from '@/api/schema/subject';
import { StatusLabel } from '@/components/patients/statusLabel';
import { parseAsNumberLiteral, useQueryState } from 'nuqs';
import { routes } from '@/types/routes';
import { PatientsContext, QueryStateKey } from '@/context/patientsContext';

export const Route = createFileRoute('/_app/patients/$patientId/')({
  loader: ({ context: { queryClient }, params: { patientId } }) => {
    return queryClient.ensureQueryData(getPatientQueryOptions(patientId));
  },
  errorComponent: ErrorComponent,
  component: RouteComponent,
});

const PRE_SCREENING_PROTOCOL = 'pre-screening';

export const TAB_INDEXES = {
  OVERVIEW: 0,
  ASSESSMENTS: 1,
  ELIGIBILITY: 2,
};

const useTabs = (t: TFunction, patientId: string, canReadAssessment: boolean, activeSubject?: Subject) => {
  const isPrescreening = activeSubject?.protocol.name.toLowerCase() === PRE_SCREENING_PROTOCOL;

  return [
    {
      index: TAB_INDEXES.OVERVIEW,
      name: t('tabs.overview', { ns: 'patients' }),
      component: <Overview />,
    },
    (activeSubject && canReadAssessment
      ? {
        index: TAB_INDEXES.ASSESSMENTS,
        name: t('tabs.assessments', { ns: 'patients' }),
        component: (
          <Assessments
            patientId={ patientId }
            subjectId={ activeSubject.id }
            isPrescreening={ isPrescreening }
            activeSubject={ activeSubject }
          />
        ),
      }
      : null),
    (activeSubject && !isPrescreening
      ? {
        index: TAB_INDEXES.ELIGIBILITY,
        name: t('tabs.eligibilityChecklist', { ns: 'patients' }),
        component: <EligibilityChecklistTab subjectId={ activeSubject.id } />,
      }
      : null),
  ].filter(isNotNull);
};

function RouteComponent() {
  const { t } = useTranslation();
  const params = Route.useParams();
  const { patientId } = params as { patientId: string };
  const { data: patient } = useSuspenseQuery(getPatientQueryOptions(patientId));
  const assessmentPermissions = usePermission('assessments.canRead', { siteId: patient.site.id });
  const canReadAssessment = !assessmentPermissions.isLoading && assessmentPermissions.hasPermissions;
  const { page: patientsListPage } = useContext(PatientsContext);

  const activeSubject = patient.subjects?.find(
    subject => subject.isActive,
  );

  // TODO make the tab filtering based on the permissions more generic
  const tabs = useTabs(t, patientId, canReadAssessment, activeSubject);
  const allowedTabs = tabs.map(tab => tab.index);
  const [ activeTab, setActiveTab ] = useQueryState('tab',
    parseAsNumberLiteral(allowedTabs).withDefault(TAB_INDEXES.OVERVIEW));

  const anonymizedView = !isFullPatient(patient);
  const title = anonymizedView
    ? patient.humanReadableId
    : `${patient.firstName} ${patient.lastName}`;

  return (
    <section className="flex flex-col flex-1">
      <header className="flex flex-col space-y-space-2xs">
        <BackButton
          linkLabel={ t('routes.patientsList') }
          href={ `${routes.patients}?${QueryStateKey}=${patientsListPage}` }
        />
        <div className="w-full flex justify-between items-center">
          <div className="flex items-center gap-space-lg">
            <h1 className="font-h1">{ title }</h1>
            <StatusLabel status={ activeSubject?.protocol.status } showNotAssigned />
          </div>
          <div className="flex flex-col items-end justify-center">
            <p className="text-body-md-semibold font-semibold">{ activeSubject?.protocol.name }</p>
            { !anonymizedView && (
              <p className="font-normal">
                { t('mrn', { ns: 'common', value: patient.externalId }) }
              </p>
            ) }
          </div>
        </div>
      </header>

      <PatientDetailsProvider patient={ patient }>
        <Tabs
          tabs={ tabs }
          activeTab={ activeTab }
          setActiveTab={ setActiveTab }
          className="mt-space-2xl !space-y-0"
        />
      </PatientDetailsProvider>
    </section>
  );
}
